<script lang="ts" setup>
import { ref, computed, type PropType } from 'vue';
import useMarkdown from '@/home/hooks/markdown';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },

  description: {
    type: String,
    default: '',
  },

  projectList: {
    type: Array as PropType<Record<string, any>[]>,
    default: () => [],
  },

  showMoreButton: {
    type: Boolean,
    default: false,
  },

  initialDisplayCount: {
    type: Number,
    default: 9,
  },
});

const marked = useMarkdown();

const lookMore = ref(false);

// 计算当前显示的项目列表
const displayList = computed(() => {
  if (!props.projectList) {
    return [];
  }

  // 如果不显示"查看更多"按钮或者已展开，显示全部
  if (!props.showMoreButton || lookMore.value) {
    return props.projectList;
  }

  // 否则只显示指定数量的项目
  return props.projectList.slice(0, props.initialDisplayCount);
});

// 是否应该显示"查看更多"按钮
const shouldShowMoreButton = computed(() => {
  return props.showMoreButton &&
         !lookMore.value &&
         props.projectList &&
         props.projectList.length > props.initialDisplayCount;
});

// 处理显示更多事件
const handleShowMore = () => {
  lookMore.value = true;
};
</script>

<template>
  <div class="container bg-white py-48" v-if="projectList?.length">
    <h2 class="m-0 text-center text-32 text-#2d2d2d font-medium leading-48">
      {{ props.title }}
    </h2>

    <!-- eslint-disable vue/no-v-html -->
    <div
      class="markdown mb-0 mt-8 break-anywhere text-center text-16 text-#2d2d2d leading-24"
      v-html="marked.parse(props.description ?? '')"
    ></div>
    <!-- eslint-enable vue/no-v-html -->

    <div class="mt-32 flex flex-wrap gap-32 <2xl:gap-24">
      <slot name="card" :data="displayList"></slot>
    </div>

    <div
      class="mt-32 h-22 flex-center text-16 text-#1180ff"
      v-if="shouldShowMoreButton"
    >
      <span class="flex-y-center cursor-pointer gap-x-4" @click="handleShowMore()">
        <span>{{ $t('projects.index.moreProjects') }}</span>
        <span class="i-home-icons:arrow-down size-16"></span>
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.markdown {
  &:deep() {
    a {
      color: #1180ff;
      text-decoration: none;
      list-style: none;
    }

    p {
      @apply m-0 mt-16 text-16 font-normal leading-24 color-hex-2d2d2d;
    }

    li {
      @apply m-0 text-16 font-normal leading-24 color-hex-2d2d2d;
    }
  }
}
</style>
